import { I18nProvider } from '@lingui/react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  ensureI18nInitialized,
  getLocale,
  getLocaleDirection,
  linguiI18n,
  setLocale,
  type SupportedLocale,
} from './i18n';
import { TovDirectionProvider } from '../components/TovDirectionProvider/TovDirectionProvider';
import { synchronizeNativeLocale } from './nativeLocale';

type LocalizationContextValue = {
  locale: SupportedLocale;
  setLocale(locale: SupportedLocale): void;
};

const LocalizationContext = createContext<LocalizationContextValue | null>(
  null,
);

export function LocalizationProvider({ children }: PropsWithChildren) {
  ensureI18nInitialized();
  const [locale, setCurrentLocale] = useState<SupportedLocale>(getLocale());
  const direction = getLocaleDirection(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    const synchronize = () => {
      const nativeWindow = window as Window & {
        flutter_inappwebview?: Parameters<typeof synchronizeNativeLocale>[1];
      };
      void synchronizeNativeLocale(locale, nativeWindow.flutter_inappwebview);
    };
    synchronize();
    window.addEventListener('flutterInAppWebViewPlatformReady', synchronize);
    return () => {
      window.removeEventListener(
        'flutterInAppWebViewPlatformReady',
        synchronize,
      );
    };
  }, [direction, locale]);

  const value = useMemo<LocalizationContextValue>(
    () => ({
      locale,
      setLocale(nextLocale) {
        setLocale(nextLocale);
        setCurrentLocale(nextLocale);
      },
    }),
    [locale],
  );

  return (
    <LocalizationContext.Provider value={value}>
      <I18nProvider i18n={linguiI18n}>
        <TovDirectionProvider key={locale} dir={direction}>
          {children}
        </TovDirectionProvider>
      </I18nProvider>
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      'useLocalization must be used within LocalizationProvider.',
    );
  }

  return context;
}

export function useLocaleDirection() {
  const context = useContext(LocalizationContext);

  return getLocaleDirection(context?.locale ?? getLocale());
}
